import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useConversationMedias } from "@/hooks/conversation/useConversationMedias";
import { useAnalyzeConversation, useMarkAsViewed, useShowConversation } from "@/hooks/conversation/useConversations";
import { supabase } from "@/integrations/supabase/client";
import { EmailService } from "@/services/communication/EmailService";
import { Conversation } from "@/types/conversation/conversation";



/**
 * Shared logic for the conversation analysis surface. Used by both
 * the side-modal (ConversationDetails) and the full-page route
 * (ConversationDetailPage). All state, effects, memos, and handlers
 * live here so the two surfaces stay in sync.
 *
 * `isActive` mirrors the old `isOpen` semantics — gates the effects
 * that subscribe / fetch on mount. Defaults to true for page mode.
 */
export function useConversationDetails(conversation: Conversation, isActive: boolean = true) {
  const [loading, setLoading] = useState(false);
  const [currentConversation, setCurrentConversation] = useState<Conversation>(conversation);
  const [emailLoading, setEmailLoading] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const queryClient = useQueryClient();
  const { mutateAsync: showConversation } = useShowConversation();
  const { mutateAsync: markAsViewed } = useMarkAsViewed();
  const { mutateAsync: analyzeConversation } = useAnalyzeConversation();
  const {
    medias: conversationMedias,
    isLoading: isLoadingConversationMedias,
    deleteMedia,
    deleteAllConversationMedias,
  } = useConversationMedias({
    conversationId: currentConversation?.elevenlabs_id || null,
    ownerConversationId: currentConversation?.id || null,
    enabled: isActive && !!currentConversation?.elevenlabs_id,
    seedMedias: currentConversation?.medias,
  });

  useEffect(() => {
    setLoading(false);
    setCurrentConversation(conversation);
  }, [conversation]);

  useEffect(() => {
    const loadInitialData = async () => {
      if (!isActive) return;
      if (conversation?.details?.id) return;

      setLoading(true);
      try {
        const { conversation: freshConv } = await showConversation(conversation);
        setCurrentConversation(freshConv);
      } catch (err: any) {
        toast.error(err.message || "Failed to load conversation data");
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, conversation]);

  useEffect(() => {
    if (!currentConversation?.elevenlabs_id) return;
    if (currentConversation?.details?.id) {
      setIsLoadingDetails(false);
      return;
    }
    if (currentConversation.metadata?.status === "done") return;

    setIsLoadingDetails(true);
    const channel = supabase.channel("conversations");

    const subscription = channel
      .on("broadcast", { event: "completed" }, async ({ payload }: { payload: any }) => {
        if (!payload?.conversation_id || payload.conversation_id !== currentConversation.id) return;

        try {
          const { conversation: refreshedConversation } = await showConversation(currentConversation);
          if (!refreshedConversation.details?.id) {
            refreshedConversation.details = payload;
          }
          setCurrentConversation(refreshedConversation);
          queryClient.invalidateQueries({ queryKey: ["conversations"] });
        } catch (error: any) {
          toast.error(error.message || "Failed to refresh conversation details");
        } finally {
          setIsLoadingDetails(false);
          channel.unsubscribe();
        }
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
      setIsLoadingDetails(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversation, queryClient]);

  const transcript = useMemo(() => {
    const rawTranscript = currentConversation?.details?.transcript || [];
    return rawTranscript.map((item: any) => {
      // Stored transcripts (recordings and genie conversations alike) use
      // { speaker, text } — preserve those fields so TranscriptTab can detect
      // and render them correctly, along with the tool calls that carry the
      // links and pictures shared on screen.
      if (item.speaker !== undefined && item.text !== undefined && item.role === undefined) {
        return {
          speaker: item.speaker,
          text: item.text,
          start: item.start,
          tool_calls: item.tool_calls || [],
          tool_results: item.tool_results || [],
        };
      }
      return {
        role: item.speaker || item.role,
        message: item.text || item.message,
        time_in_call_secs: item.timestamp || item.time_in_call_secs,
        tool_calls: item.tool_calls || [],
        tool_results: item.tool_results || [],
      };
    });
  }, [currentConversation?.details?.transcript]);

  const analysisResult = currentConversation?.details?.analysis_result;
  const topics = analysisResult?.main_topics || currentConversation?.details?.topics || [];
  const insights = analysisResult?.key_insights || [];

  const supportTeamEmails = useMemo(
    () => (currentConversation?.agent as any)?.report_recipients || [],
    [currentConversation?.agent],
  );

  const isAnalyzeDisabled = false;

  const shouldShowLoadingForAnalysis = false;

  const getAnalyzeDisabledReason = null;

  const handleRunAI = async () => {
    if (isAnalyzeDisabled) {
      toast.error(getAnalyzeDisabledReason || "Cannot run analysis at this time");
      return;
    }
    if (!currentConversation?.id) {
      toast.error("Conversation ID is missing, cannot run analysis.");
      return;
    }
    setLoading(true);
    try {
      const result = await analyzeConversation({ conversation: currentConversation });
      setCurrentConversation({
        ...currentConversation,
        details: { ...currentConversation.details, ...result },
      });
      toast.success("Analysis updated. See the Insights tab.");
    } catch (e: any) {
      toast.error(e.message || "Failed to analyze conversation");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsReviewed = async () => {
    setReviewLoading(true);
    try {
      await markAsViewed(currentConversation);
      setCurrentConversation({ ...currentConversation, viewed: true });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      toast.success("Conversation marked as reviewed");
    } catch (error: any) {
      toast.error(`Failed to mark as reviewed: ${error.message}`);
    } finally {
      setReviewLoading(false);
    }
  };

  const handleDeleteConversationMedia = async (mediaId: string) => {
    const ok = await deleteMedia(mediaId);
    if (!ok) {
      toast.error("Failed to delete image");
      return false;
    }
    toast.success("Image deleted");
    return true;
  };

  const handleDeleteAllConversationMedias = async () => {
    const ok = await deleteAllConversationMedias();
    if (!ok) {
      toast.error("Failed to delete all images");
      return false;
    }
    toast.success("All conversation images deleted");
    return true;
  };

  const handleSendEmail = async () => {
    setEmailLoading(true);
    try {
      const result = await EmailService.sendAnalysisEmail(currentConversation);
      toast.success(`Email sent successfully to ${result.recipientCount} recipient(s)!`);
    } catch (error: any) {
      toast.error(`Failed to send email: ${error.message}`);
    } finally {
      setEmailLoading(false);
    }
  };

  return {
    currentConversation,
    loading,
    emailLoading,
    reviewLoading,
    isLoadingDetails,
    isLoadingConversationMedias,
    conversationMedias,
    transcript,
    analysisResult,
    topics,
    insights,
    supportTeamEmails,
    isAnalyzeDisabled,
    shouldShowLoadingForAnalysis,
    getAnalyzeDisabledReason,
    handleRunAI,
    handleMarkAsReviewed,
    handleSendEmail,
    handleDeleteConversationMedia,
    handleDeleteAllConversationMedias,
  };
}

export type UseConversationDetailsReturn = ReturnType<typeof useConversationDetails>;
